/**
 * Redemptions tab -- phase 6b, Task 3. Read-only audit list for one
 * promotion: GET /admin/discounts/redemptions?promotionId=… with a status
 * filter, server-paged.
 *
 * `user: null` means the customer account was deleted since (rendered
 * "Deleted account", never blank). The order is always real -- a redemption
 * whose order was purged is skipped server-side (ADMIN_API.md) -- so the
 * order number links to the existing react-admin orders page
 * (`<Resource name="orders" edit=…>` -> `/orders/:id`).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, Link, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { discountsApi, qk } from '../api';
import { toApiFailure } from '../errors';
import { fmtDateTime } from '../format';
import { gridTestProps } from '../gridTestProps';
import { formatRand } from '../money';
import { useScrollToListTop } from '../../../hooks/useScrollToListTop';
import type { ListRedemptionsQuery, RedemptionRow, RedemptionStatus } from '../types';

export const STATUS_LABEL: Record<RedemptionStatus, string> = {
  RESERVED: 'Reserved',
  CONFIRMED: 'Confirmed',
  ROLLED_BACK: 'Rolled back',
};

const STATUS_COLOR: Record<RedemptionStatus, 'default' | 'success' | 'warning'> = {
  RESERVED: 'warning',
  CONFIRMED: 'success',
  ROLLED_BACK: 'default',
};

export const DELETED_ACCOUNT = 'Deleted account';

export interface RedemptionsTabProps {
  promotionId: string;
}

export const RedemptionsTab: React.FC<RedemptionsTabProps> = ({ promotionId }) => {
  const [status, setStatus] = useState<RedemptionStatus | ''>('');
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const listTopRef = useScrollToListTop(pagination.page);

  useEffect(() => {
    setPagination((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  }, [status]);

  const query = useMemo<ListRedemptionsQuery>(
    () => ({ promotionId, status: status || undefined, page: pagination.page + 1, limit: pagination.pageSize }),
    [promotionId, status, pagination],
  );

  const list = useQuery({
    queryKey: qk.redemptions(query),
    queryFn: () => discountsApi.listRedemptions(query),
    placeholderData: keepPreviousData,
  });

  const columns = useMemo<GridColDef<RedemptionRow>[]>(
    () => [
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: ({ value }) => (
          <Chip label={STATUS_LABEL[value as RedemptionStatus] ?? value} size="small" color={STATUS_COLOR[value as RedemptionStatus] ?? 'default'} />
        ),
      },
      { field: 'amount', headerName: 'Amount', width: 120, align: 'right', headerAlign: 'right', valueGetter: (v: string) => formatRand(v) },
      {
        field: 'code',
        headerName: 'Code',
        flex: 1,
        minWidth: 160,
        valueGetter: (_v, row) => row.voucher.code,
        renderCell: ({ row }) => (
          <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 13 }} noWrap>
            {row.voucher.code}
          </Typography>
        ),
      },
      {
        field: 'customer',
        headerName: 'Customer',
        flex: 1.4,
        minWidth: 220,
        sortable: false,
        renderCell: ({ row }) =>
          row.user ? (
            <Box sx={{ minWidth: 0 }}>
              <Link component={RouterLink} to={`/discounts/customers/${row.user.id}`} underline="hover" variant="body2" noWrap display="block">
                {row.user.firstName}
              </Link>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {row.user.email}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              {DELETED_ACCOUNT}
            </Typography>
          ),
      },
      {
        field: 'order',
        headerName: 'Order',
        width: 160,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Link component={RouterLink} to={`/orders/${row.order.id}`} underline="hover" variant="body2" noWrap>
              {row.order.number ?? row.order.id.slice(0, 8)}
            </Link>
            {!row.order.isPaid && <Chip label="Unpaid" size="small" variant="outlined" />}
          </Stack>
        ),
      },
      { field: 'createdAt', headerName: 'Reserved', width: 170, valueGetter: (v: string) => fmtDateTime(v) },
      { field: 'confirmedAt', headerName: 'Confirmed', width: 170, valueGetter: (v: string | null) => fmtDateTime(v) },
      { field: 'rolledBackAt', headerName: 'Rolled back', width: 170, valueGetter: (v: string | null) => fmtDateTime(v) },
    ],
    [],
  );

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          select
          label="Status"
          size="small"
          value={status}
          onChange={(e) => setStatus(e.target.value as RedemptionStatus | '')}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">All statuses</MenuItem>
          {(Object.keys(STATUS_LABEL) as RedemptionStatus[]).map((s) => (
            <MenuItem key={s} value={s}>
              {STATUS_LABEL[s]}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {list.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load redemptions: {toApiFailure(list.error).message}
        </Alert>
      )}

      <Box ref={listTopRef} sx={{ width: '100%' }}>
        <DataGrid
          rows={list.data?.data ?? []}
          columns={columns}
          loading={list.isFetching}
          paginationMode="server"
          rowCount={list.data?.total ?? 0}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          disableColumnMenu
          autoHeight
          getRowHeight={() => 'auto'}
          {...gridTestProps()}
          localeText={{ noRowsLabel: 'No redemptions match this filter.' }}
          sx={{ '& .MuiDataGrid-cell': { py: 0.75 } }}
        />
      </Box>
    </Box>
  );
};

export default RedemptionsTab;
